import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { getRootAttribute } from "../utils";
import _ from "lodash";

const WebSocketContext = createContext(null);

export const WebSocketProvider = ({ children }) => {
  const webSocket = useRef(null);
  const [latestMessage, setLatestMessage] = useState(null);
  const [latestSyncAdministrativeFieldsResponse, setLatestSyncAdministrativeFieldsResponse] = useState(null);
  const [isWebSocketConnected, setIsWebSocketConnected] = useState(false);
  const [token, setToken] = useState(null);

  // Generate WebSocket base URL
  let websocketUrlTemp = getRootAttribute("data-websocket-url");
  if (!websocketUrlTemp) {
    websocketUrlTemp = process.env.REACT_APP_WEBSOCKET_URL;
    if (!websocketUrlTemp) {
      const appName = window.location.pathname.substring(0, window.location.pathname.lastIndexOf("/") + 1);
      const path = window.location.hostname + ":" + window.location.port + appName + "websocket";
      websocketUrlTemp = window.location.protocol.startsWith("https") ? "wss://" + path : "ws://" + path;
    }
  }

  const webSocketUrl = websocketUrlTemp;

  // 🔁 Listen for token from localStorage or custom event
  useEffect(() => {
    const handleTokenReady = (e) => {
      const receivedToken = e.detail;
      setToken(receivedToken);
    };

    // ✅ Listen to token-ready event
    window.addEventListener("token-ready", handleTokenReady);

    // ✅ If token already exists (e.g. after refresh), use it
    const storedToken = localStorage.getItem("tok");
    if (storedToken) {
      setToken(storedToken);
    }

    return () => {
      window.removeEventListener("token-ready", handleTokenReady);
    };
  }, []);

  // 🔌 Connect WebSocket when token is available
  useEffect(() => {
    if (!token) return;
// ?token=${token}
    const applicationWebSocketUrl = `${webSocketUrl}`;
    console.log("--> websocket url connection: " + applicationWebSocketUrl);

    webSocket.current = new WebSocket(applicationWebSocketUrl);

    webSocket.current.onopen = () => {
      console.log("WebSocket Connected");
      setIsWebSocketConnected(true);
    };

    webSocket.current.onmessage = (event) => {
      const newMessage = event.data;

      let parsedMessage = JSON.parse(newMessage);
      if (typeof parsedMessage === 'string') {
        parsedMessage = JSON.parse(parsedMessage);
      }

      const command = parsedMessage.command;

      if (command === 'syncAdministrativeFieldsResponse' && !_.isEqual(latestSyncAdministrativeFieldsResponse, newMessage)) {
        setLatestSyncAdministrativeFieldsResponse(newMessage);
        setLatestMessage(newMessage);
      } else if (command === 'pong') {
        console.log('Received pong from server');
        if (window.conference?.requestSyncAdministrativeFields) {
          window.conference.requestSyncAdministrativeFields();
        }
      } else {
        setLatestMessage(newMessage);
      }
    };

    webSocket.current.onclose = () => {
      console.log("WebSocket Disconnected");
      setIsWebSocketConnected(false);
    };

    webSocket.current.onerror = (error) => {
      console.error("WebSocket Error", error);
    };

    const pingInterval = setInterval(() => {
      if (webSocket.current?.readyState === WebSocket.OPEN) {
        webSocket.current.send(JSON.stringify({ command: "ping" }));
      } else if (webSocket.current?.readyState === WebSocket.CLOSED) {
        console.log("WebSocket not connected, attempting to reconnect");
        webSocket.current = new WebSocket(applicationWebSocketUrl);
      }
    }, 5000);

    return () => {
      webSocket.current?.close();
      clearInterval(pingInterval);
    };
  }, [token]);

  const sendMessage = (message) => {
    if (webSocket.current && isWebSocketConnected) {
      webSocket.current.send(message);
    }
  };

  return (
    <WebSocketContext.Provider value={{ sendMessage, latestMessage, isWebSocketConnected }}>
      {children}
    </WebSocketContext.Provider>
  );
};

export const useWebSocket = () => {
  return useContext(WebSocketContext);
};
